import moment from 'moment';
import { patientService } from '../../patient/services/patient-service';
import { ClientCrm, ClientStatusCardViewModel } from '../../shared/model/client-status-card';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Enroll service for Payment Hub V3 flow.
 * 
 * This handles:
 * 1. Generate documents (SPA or installment)
 * 2. Update workflow status (mark as "Converted" or "Paid")
 * 3. Update IOC (financial clearance status, convert date, convertUserId, etc.)
 * 4. Trigger CRM integration (if enabled)
 * 
 * Call this AFTER successful payment processing.
 * 
 * Utility functions in hub-utils.ts:
 * - isPaidInFull(program) - check if pfrPendingBalance === 0 AFTER payment
 * - isZeroDownPayment(program) - check if program has $0 down payment
 * - getZeroDownInfo(encounter) - get all zero-down related info
 * - getPaymentCompletionInfo(program) - get payment status after payment
 * 
 * Excluded from this flow (handled elsewhere or not needed for V3):
 * - Creating payment records (V3 handles this via payment-service)
 * - Capturing pending payments (V3 handles this differently)
 * - Payment schedule updates (handled by program-service)
 * - Create/update user accounts (handled separately)
 * - Send notifications (handled separately)
 * - Refresh data (caller should handle)
 */

export interface EnrollParams {
  patientId: number;
  patientEncounterId: number;
  programId: number;
  isPaidInFull: boolean;
  client?: ClientStatusCardViewModel;
  skipDocuments?: boolean;
  guarantorEmail?: string;
  guarantorUserId?: number;
}

export interface EnrollResult {
  success: boolean;
  message: string;
  documentSent?: boolean;
  workflowUpdated?: boolean;
  iocUpdated?: boolean;
  linkedUserUpdated?: boolean;
  integrationTriggered?: boolean;
}

// Workflow status IDs
const WORKFLOW_STATUS = {
  PAID: 16,
  CONVERTED: 12,
} as const;

// Workflow sub-status IDs
const WORKFLOW_SUB_STATUS = {
  NONE: 32,
  CONVERTED_NONE: 23,
} as const;


export const executeEnroll = async (params: EnrollParams): Promise<EnrollResult> => {
  const {
    patientId,
    patientEncounterId,
    isPaidInFull,
    programId,
    skipDocuments = false,
    client,
    guarantorEmail,
    guarantorUserId,
  } = params;

  const result: EnrollResult = {
    success: false,
    message: '',
  };

  // Get logged-in user email for convertUserId
  let convertUserId: string | undefined;
  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;
    convertUserId = payload?.email as string | undefined;
    
    console.log('[EnrollService] Fetched convertUserId (email) from session:', {
      convertUserId,
      sub: payload?.sub,
    });
  } catch (error) {
    console.error('[EnrollService] Failed to fetch user session for convertUserId:', error);
  }

  if (!convertUserId) {
    return {
      ...result,
      success: false,
      message: 'Failed to retrieve user email for enrollment',
    };
  }

  const clientCrm = client?.clientCrm;
  const isIntegrationEnabled = clientCrm?.some((crm: ClientCrm) => crm.isIntEnabled);

  try {
    // 1. Generate and send documents (if not skipped)
    if (!skipDocuments) {
      const documentResult = await generateDocument({
        patientEncounterId,
        programId,
        isPaidInFull,
      });

      if (!documentResult.success) {
        throw new Error(`Failed to generate document: ${documentResult.message}`);
      }
      result.documentSent = true;
    }

    // 2. Update workflow status
    const workflowResult = await updateWorkflowStatus({
      patientId,
      patientEncounterId,
      isPaidInFull,
    });

    if (!workflowResult.success) {
      throw new Error(`Failed to update workflow status: ${workflowResult.message}`);
    }
    result.workflowUpdated = true;

    // 3. Update IOC with financial clearance status and linked user data (single API call)
    const iocResult = await updateIOCStatus({
      patientEncounterId,
      isPaidInFull,
      convertUserId,
      linkedUserId: guarantorEmail,
      accountHolderUserId: guarantorUserId,
    });

    if (!iocResult.success) {
      throw new Error(`Failed to update IOC status: ${iocResult.message}`);
    }
    result.iocUpdated = true;
    if (guarantorEmail || guarantorUserId) {
      result.linkedUserUpdated = true;
    }

    // 4. Trigger CRM integration (if enabled)
    if (isIntegrationEnabled && clientCrm) {
      const crmSlug = clientCrm.find((crm: ClientCrm) => crm.isIntEnabled)?.crmType?.crmTypeSlug;
      if (crmSlug) {
        try {
          await patientService.integrationUpdate({
            patientEncounterId,
            patientId,
            crmTypeSlug: crmSlug,
          });
          result.integrationTriggered = true;
        } catch (integrationError) {
          // Integration failure shouldn't fail the whole enroll
          console.warn('[EnrollService] CRM integration failed:', integrationError);
        }
      }
    }

    // All critical steps succeeded
    result.success = true;
    result.message = isPaidInFull 
      ? 'Enrollment completed - Paid in Full' 
      : 'Enrollment completed - Payment Plan Active';

    return result;

  } catch (error: any) {
    console.error('[EnrollService] Enroll failed with exception:', error);
    return {
      ...result,
      success: false,
      message: error.message || 'An error occurred during enrollment',
    };
  }
};

/**
 * Generate and send the appropriate document (SPA for full pay, Installment for payment plan)
 */
interface GenerateDocumentParams {
  patientEncounterId: number;
  programId: number;
  isPaidInFull: boolean;
}

interface GenerateDocumentResult {
  success: boolean;
  message: string;
}

const generateDocument = async (params: GenerateDocumentParams): Promise<GenerateDocumentResult> => {
  const { patientEncounterId, programId, isPaidInFull } = params;

  try {
    let documentResponse;

    if (isPaidInFull) {
      // Full payment - send SPA (Single Payment Agreement) document
      documentResponse = await patientService.spaDocument(patientEncounterId);
    } else {
      // Payment plan - send Installment document
      documentResponse = await patientService.installmentDocument(patientEncounterId);
    }

    if (documentResponse.hasErrors) {
      return {
        success: false,
        message: documentResponse.entity?.message || documentResponse.errorMessage || 'Document generation failed',
      };
    }

    return {
      success: true,
      message: documentResponse.entity?.message || 'Document sent successfully',
    };

  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'An error occurred generating document',
    };
  }
};

/**
 * Update the workflow status based on payment outcome
 */
interface UpdateWorkflowParams {
  patientId: number;
  patientEncounterId: number;
  isPaidInFull: boolean;
}

interface UpdateWorkflowResult {
  success: boolean;
  message: string;
}

const updateWorkflowStatus = async (params: UpdateWorkflowParams): Promise<UpdateWorkflowResult> => {
  const { patientId, patientEncounterId, isPaidInFull } = params;

  try {
    const statusResult = await patientService.saveStatus({
      workFlow: {
        workflowId: 3, // Billing workflow
        workflowStatus: {
          workflowStatusId: isPaidInFull ? WORKFLOW_STATUS.PAID : WORKFLOW_STATUS.CONVERTED,
          workflowStatusDesc: '',
        },
        workflowSubStatus: {
          workflowSubStatusId: isPaidInFull ? WORKFLOW_SUB_STATUS.NONE : WORKFLOW_SUB_STATUS.CONVERTED_NONE,
          workflowSubStatusDesc: '',
        },
      },
      encounterId: patientEncounterId,
      patientId,
    });

    if (statusResult.hasErrors) {
      return {
        success: false,
        message: statusResult.entity || 'Failed to update workflow status',
      };
    }

    return {
      success: true,
      message: 'Workflow status updated',
    };

  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'An error occurred updating workflow status',
    };
  }
};

/**
 * Update the IOC with financial clearance status, conversion info, and linked user data
 */
interface UpdateIOCParams {
  patientEncounterId: number;
  isPaidInFull: boolean;
  convertUserId: string; // Email of logged-in user performing enrollment
  linkedUserId?: string; // Email of guarantor (must exist in users.user_name)
  accountHolderUserId?: number; // User ID of guarantor (must exist in users.user_id)
}

interface UpdateIOCResult {
  success: boolean;
  message: string;
}

const updateIOCStatus = async (params: UpdateIOCParams): Promise<UpdateIOCResult> => {
  const { patientEncounterId, isPaidInFull, convertUserId, linkedUserId, accountHolderUserId } = params;

  try {
    const now = moment().toISOString();

    const iocUpdatePayload: Record<string, any> = {
      finClearanceStatus: isPaidInFull ? 'Paid, None' : 'Converted',
      finClearanceStatusDt: now,
      convertDt: now,
      convertUserId: convertUserId, // Email of logged-in user
      isConverted: true,
    };

    if (linkedUserId) {
      iocUpdatePayload.linkedUserId = linkedUserId;
    }
    if (accountHolderUserId) {
      iocUpdatePayload.accountHolderUserId = accountHolderUserId;
    }

    console.log('[EnrollService] Updating IOC (single call):', {
      patientEncounterId,
      ...iocUpdatePayload,
    });

    const iocResult = await patientService.saveIOCWithPartialObject(
      iocUpdatePayload,
      patientEncounterId
    );

    if (iocResult?.hasErrors) {
      return {
        success: false,
        message: iocResult.entity || 'Failed to update IOC status',
      };
    }

    return {
      success: true,
      message: 'IOC status updated',
    };

  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'An error occurred updating IOC status',
    };
  }
};
